import { useState } from "react";
import { toast } from "react-toastify";
import { updateFight } from "../../services/fightsAPI";
import { searchPlayers } from "../../services/playersAPI";
import { AsyncDropdown } from "../ui/forms/dropdown/AsyncDropdown";
import { toPlayerOption } from "../ui/forms/dropdown/ddLabels";
import { YouTubeVideoInput } from "../ui/forms/files/YouTubeVideoInput";
import { Button } from "../ui/btns/Button";
import { Modal } from "./Modal";
import { useQueryClient } from "@tanstack/react-query";

const UpdateFight = ({ fightID, fight, isOpen, close }) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    player1: fight.player1,
    player2: fight.player2,
    fightType: fight.fightType || "",
    time: fight.time || "",
    period: fight.period || "",
    date: fight.date || "",
    description: fight.description || "",
    eventDescription: fight.eventDescription || "",
    tookPlaceAt: fight.tookPlaceAt || "",
    videoLink: fight.videoLink || "",
    unfair: fight.unfair || false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePlayerChange = (option, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: option ? option.value : null,
    }));
  };

  const handleYouTubeUrl = (url) => {
    setFormData((prev) => ({ ...prev, videoLink: url }));
  };

  const validate = () => {
    if (formData.time && formData.time !== "") {
      const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
      if (!timeRegex.test(formData.time)) {
        return "Invalid time format. Use HH:MM (24-hour)";
      }
    }
    if (formData.player1 && formData.player2 && formData.player1 === formData.player2) {
      return "Player 1 and Player 2 cannot be the same";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Only send changed fields
    const changes = {};
    for (const [key, value] of Object.entries(formData)) {
      if (value !== (fight[key] ?? "")) {
        changes[key] = value;
      }
    }

    if (Object.keys(changes).length === 0) {
      close();
      return;
    }

    setIsLoading(true);
    try {
      await updateFight(fightID, changes);
      toast.success("Fight updated successfully");
      queryClient.invalidateQueries({ queryKey: ["fight", fightID] });
      close();
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || "Error updating fight";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const p1Default = fight.Player1Details
    ? { label: `${fight.Player1Details.firstName} ${fight.Player1Details.lastName}`, value: fight.Player1Details.PlayerID }
    : null;

  const p2Default = fight.Player2Details
    ? { label: `${fight.Player2Details.firstName} ${fight.Player2Details.lastName}`, value: fight.Player2Details.PlayerID }
    : null;

  return (
    <Modal close={close} isOpen={isOpen}>
      <div className="p-5">
        <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-flyers-light-gray mb-4">
          Edit Fight Details
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-500 rounded text-red-400 text-sm font-body">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Player 1 */}
          <div>
            <label className="label">Player 1</label>
            <AsyncDropdown
              name="player1"
              placeholder="Select Player 1"
              handleChange={handlePlayerChange}
              isSearchable={true}
              apiFunction={searchPlayers}
              toOptionLabel={toPlayerOption}
              defaultValue={p1Default}
            />
          </div>

          {/* Player 2 */}
          <div>
            <label className="label">Player 2</label>
            <AsyncDropdown
              name="player2"
              placeholder="Select Player 2"
              handleChange={handlePlayerChange}
              isSearchable={true}
              apiFunction={searchPlayers}
              toOptionLabel={toPlayerOption}
              defaultValue={p2Default}
            />
          </div>

          {/* Fight Type */}
          <div>
            <label className="label">Fight Type</label>
            <input
              type="text"
              name="fightType"
              value={formData.fightType}
              onChange={handleChange}
              className="w-full p-2 bg-flyers-charcoal text-white border border-flyers-dark-gray rounded focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
              placeholder="e.g., Staged, Spontaneous"
            />
          </div>

          {/* Time */}
          <div>
            <label className="label">Time (HH:MM)</label>
            <input
              type="text"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="w-full p-2 bg-flyers-charcoal text-white border border-flyers-dark-gray rounded focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
              placeholder="e.g., 05:30"
            />
          </div>

          {/* Period */}
          <div>
            <label className="label">Period</label>
            <input
              type="text"
              name="period"
              value={formData.period}
              onChange={handleChange}
              className="w-full p-2 bg-flyers-charcoal text-white border border-flyers-dark-gray rounded focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
              placeholder="e.g., 1st Period, OT"
            />
          </div>

          {/* Date */}
          <div>
            <label className="label">Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full p-2 bg-flyers-charcoal text-white border border-flyers-dark-gray rounded focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
            />
          </div>

          {/* Description */}
          <div>
            <label className="label">Description</label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full p-2 bg-flyers-charcoal text-white border border-flyers-dark-gray rounded focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
              placeholder="Fight description"
            />
          </div>

          {/* Event Description */}
          <div>
            <label className="label">Event Description</label>
            <input
              type="text"
              name="eventDescription"
              value={formData.eventDescription}
              onChange={handleChange}
              className="w-full p-2 bg-flyers-charcoal text-white border border-flyers-dark-gray rounded focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
              placeholder="Event description"
            />
          </div>

          {/* Took Place At */}
          <div>
            <label className="label">Took Place At</label>
            <input
              type="text"
              name="tookPlaceAt"
              value={formData.tookPlaceAt}
              onChange={handleChange}
              className="w-full p-2 bg-flyers-charcoal text-white border border-flyers-dark-gray rounded focus:border-flyers-orange focus:outline-none placeholder-flyers-mid-gray"
              placeholder="Location"
            />
          </div>

          {/* YouTube URL */}
          <div>
            <YouTubeVideoInput
              setYouTubeUrl={handleYouTubeUrl}
              initialValue={formData.videoLink}
            />
          </div>

          {/* Unfair */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              name="unfair"
              checked={formData.unfair}
              onChange={handleChange}
              className="w-4 h-4 accent-flyers-orange"
              id="unfair-checkbox"
            />
            <label htmlFor="unfair-checkbox" className="label mb-0">
              Unfair Fight
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 justify-end pt-3">
            <Button onClick={close} variant="secondary" type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default UpdateFight;
